/**
 * StoragePersist.ts
 * Manages acquisition, verification, and diagnostics of durable client-side storage
 * via the W3C StorageManager API (navigator.storage.persist).
 */

export interface StorageDiagnostic {
  isPersisted: boolean;
  quotaBytes?: number;
  usageBytes?: number;
  availableMB?: number;
  usagePercent?: number;
}

/**
 * Requests persistent storage from the browser to prevent eviction under low disk pressure.
 * Returns true if persistence is confirmed or granted, false if rejected or unsupported.
 */
export async function ensureStoragePersistence(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.storage) {
    console.warn('[StoragePersist] StorageManager API unsupported in this environment.');
    return false;
  }

  try {
    // 1. Check if already persisted
    if (typeof navigator.storage.persisted === 'function') {
      const alreadyPersisted = await navigator.storage.persisted();
      if (alreadyPersisted) {
        console.log('[StoragePersist] Storage is already persisted.');
        return true;
      }
    }

    // 2. Request persistence
    if (typeof navigator.storage.persist === 'function') {
      const granted = await navigator.storage.persist();
      console.log(`[StoragePersist] Persistent storage request result: ${granted}`);
      return granted;
    }

    return false;
  } catch (err) {
    console.warn('[StoragePersist] Error acquiring storage persistence:', err);
    return false;
  }
}

/**
 * Checks whether persistent storage is currently active.
 */
export async function checkStoragePersisted(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.storage || typeof navigator.storage.persisted !== 'function') {
    return false;
  }

  try {
    return await navigator.storage.persisted();
  } catch {
    return false;
  }
}

/**
 * Retrieves storage quota diagnostics (quota, usage, and available MB).
 */
export async function getStorageDiagnostics(): Promise<StorageDiagnostic> {
  let isPersisted = false;
  let quotaBytes: number | undefined;
  let usageBytes: number | undefined;

  try {
    isPersisted = await checkStoragePersisted();

    if (typeof navigator !== 'undefined' && navigator.storage && typeof navigator.storage.estimate === 'function') {
      const estimate = await navigator.storage.estimate();
      quotaBytes = estimate.quota;
      usageBytes = estimate.usage;
    }
  } catch (err) {
    console.warn('[StoragePersist] Error calculating storage diagnostics:', err);
  }

  const availableMB =
    quotaBytes !== undefined && usageBytes !== undefined
      ? Math.max(0, Math.round((quotaBytes - usageBytes) / (1024 * 1024)))
      : undefined;

  const usagePercent =
    quotaBytes !== undefined && usageBytes !== undefined && quotaBytes > 0
      ? Math.min(100, Math.round((usageBytes / quotaBytes) * 100))
      : undefined;

  return {
    isPersisted,
    quotaBytes,
    usageBytes,
    availableMB,
    usagePercent
  };
}
